import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useUrlLocalizer, useTranslator } from 'tradera-lang/translate';
import { useInView } from 'react-intersection-observer';
import useIsMobile from 'tradera-hooks/use-is-mobile';
import useIsDesktop from 'tradera-hooks/use-is-desktop';
import ALink from 'tradera-components/alink/alink';
import ItemCard from 'tradera-components/item-card/item-card';
import { lazyLoadSettings } from 'tradera-components/item-card/config';
import { generateItemCardPlaceholders } from 'tradera-components/item-card/generate-item-card-placeholders';
import itemActions from 'tradera-state/items/actions';
import uiActions from 'tradera-state/ui/actions';
import { trackContainerClick } from '../analytics';
import { fetchFavouriteSellers } from 'tradera-state/views/start-page/actions';
import FavouriteSellersFallback from './favourite-sellers-fallback';
import 'tradera-components/item-card/layout.scss';
import './favourite-sellers.scss';
import useIsLoggedIn from 'tradera-hooks/use-is-loggedin';
import useSplitTestGroup from 'tradera-hooks/use-split-test-group';
import { SellerSuggestions } from './seller-suggestions';
import { FavouriteSellerCardsGrid } from './favourite-seller-cards-grid';
import { useIsFeatureEnabled } from 'tradera-hooks/use-is-feature-enabled';

const FavouriteSellers = () => {
    const isLoggedIn = useIsLoggedIn();

    return isLoggedIn ? <FavouriteSellersInternal /> : null;
};

const FavouriteSellersInternal = () => {
    const inSPA = true;
    const { t } = useTranslator();
    const dispatch = useDispatch();
    const isSellerListEnabled = useIsFeatureEnabled('my-tradera-seller-list');

    const [loadingTriggerRef, loadingTriggerInView] = useInView({
        triggerOnce: true
    });

    useEffect(() => {
        if (loadingTriggerInView) {
            dispatch(fetchFavouriteSellers());
        }
    }, [dispatch, loadingTriggerInView]);

    const itemCount = 8;
    const {
        sellers: allFavouriteSellers,
        sellerSuggestions = [],
        itemIds,
        status
    } = useSelector(state => state.views.startPage.favouriteSellers);

    const items = useSelector(state => state.items.cache);
    const isMobile = useIsMobile();
    const isDesktop = useIsDesktop();

    const favouriteSellers = isMobile
        ? allFavouriteSellers
        : allFavouriteSellers?.slice(0, isDesktop ? 4 : 3);

    const sellerSuggestionsPosition = useSplitTestGroup(
        'seller_suggestions_position'
    );
    const showSellerSuggestionsPositionAbove =
        sellerSuggestionsPosition === 'above';
    const showSellerSuggestionsPositionBelow =
        sellerSuggestionsPosition === 'control' ||
        sellerSuggestionsPosition === 'below';

    const resolveItems = (itemIds = [], itemsMap = {}, maxItemsCount = 0) => {
        const mappedItems = itemIds.map(itemId => itemsMap[itemId]);
        return mappedItems.filter(item => !!item).slice(0, maxItemsCount);
    };

    const displayItems = ['NOT_FETCHED', 'LOADING'].includes(status)
        ? generateItemCardPlaceholders(itemCount)
        : resolveItems(itemIds, items, itemCount);

    const toggleWishList = itemId => {
        dispatch(itemActions.wishlist(itemId));
    };

    const openQuickView = itemId =>
        dispatch(
            uiActions.openQuickView({
                itemIds,
                itemId,
                openedFrom: 'Start Page Favourite Sellers'
            })
        );

    const { toLocalizedUrl } = useUrlLocalizer();

    const showFallbackInfo = status === 'HAS_NO_DATA';

    return (
        <div ref={loadingTriggerRef}>
            {showSellerSuggestionsPositionAbove && (
                <SellerSuggestions sellerSuggestions={sellerSuggestions} />
            )}
            <section className="pt-5">
                <div className="d-flex justify-content-between mb-2">
                    <h3 className="start-page-section-title m-0">
                        {t('startPage_favouriteSellers_title')}
                    </h3>
                    <ALink
                        className="btn btn-link mr-2 mr-md-3 mr-xl-2 h-auto"
                        href={toLocalizedUrl(
                            isSellerListEnabled
                                ? 'my/seller-list'
                                : '/my/favourite-sellers'
                        )}
                        onClick={() =>
                            trackContainerClick(
                                'favouriteSellers',
                                'Favourite-Sellers showAll link'
                            )
                        }
                        useLink={false} // Use 'inSPA' when mytradera-page can be rnavigated to without a hard reload
                    >
                        {`${t('startPage_showAll_link')} ->`}
                    </ALink>
                </div>
                {showFallbackInfo ? (
                    <FavouriteSellersFallback />
                ) : (
                    <>
                        <FavouriteSellerCardsGrid
                            sellers={favouriteSellers}
                            className="mb-4"
                        />
                        <div
                            className={`item-card-layout d-flex ${
                                isMobile
                                    ? 'layout-scroll horizontal-scroll flex-nowrap overflow-x-auto'
                                    : 'layout-grid flex-wrap'
                            }`}>
                            {displayItems.map(item => (
                                <div
                                    key={item.itemId}
                                    className="item-card-container flex-shrink-0 pb-2">
                                    <ItemCard
                                        item={item}
                                        lazy={lazyLoadSettings}
                                        onAddToWishlist={toggleWishList}
                                        onQuickView={openQuickView}
                                        onRemoveFromWishlist={toggleWishList}
                                        ready={!!item.itemUrl}
                                        gaCategory="Startpage wish list"
                                        showBidStatus={true}
                                        showShippingOrOnDisplay={false}
                                        showSellerAlias={true}
                                    />
                                </div>
                            ))}
                            {isMobile && (
                                <div
                                    className="flex-shrink-0 px-3 pb-2 d-flex flex-column justify-content-center"
                                    style={{
                                        marginBottom: '2rem'
                                    }}>
                                    <ALink
                                        className="btn btn-primary"
                                        href={toLocalizedUrl(
                                            '/favourite-sellers'
                                        )}
                                        onClick={() =>
                                            trackContainerClick(
                                                'favouriteSellers',
                                                'Favourite-Sellers button below'
                                            )
                                        }
                                        useLink={inSPA}>
                                        {`${t(
                                            'startPage_favouriteSellers_showMore'
                                        )} ->`}
                                    </ALink>
                                </div>
                            )}
                        </div>
                        {!isMobile && (
                            <div className="text-center">
                                <ALink
                                    className="btn btn-primary btn-wide"
                                    href={toLocalizedUrl('/favourite-sellers')}
                                    onClick={() =>
                                        trackContainerClick(
                                            'favouriteSellers',
                                            'Favourite-Sellers button below'
                                        )
                                    }
                                    useLink={inSPA}>
                                    {`${t(
                                        'startPage_favouriteSellers_showMore'
                                    )} ->`}
                                </ALink>
                            </div>
                        )}
                    </>
                )}
            </section>
            {showSellerSuggestionsPositionBelow && (
                <SellerSuggestions sellerSuggestions={sellerSuggestions} />
            )}
        </div>
    );
};

export default FavouriteSellers;
